<!-- src/apps/colonel/views/ColonelSystemMainDB.vue -->

<script setup lang="ts">
  import { useColonelInfoStore } from '@/shared/stores/colonelInfoStore';
  import { storeToRefs } from 'pinia';
  import { computed, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  const store = useColonelInfoStore();
  const { databaseMetrics, loading } = storeToRefs(store);
  const { fetchDatabaseMetrics } = store;

  onMounted(() => fetchDatabaseMetrics());

  // Valkey reports both valkey_version and a Redis-compat redis_version.
  // Prefer Valkey when present so the page reflects the real engine
  // rather than the compatibility shim version (e.g. 7.2.4).
  const engineName = computed(() =>
    databaseMetrics.value?.redis_info.valkey_version ? 'Valkey' : 'Redis'
  );
  const engineVersion = computed(
    () =>
      databaseMetrics.value?.redis_info.valkey_version ??
      databaseMetrics.value?.redis_info.redis_version
  );
</script>

<template>
  <div>
    <div
      v-if="loading.databaseMetrics"
      class="text-center">
      {{ t('web.LABELS.loading') }}
    </div>

    <div v-else-if="databaseMetrics">
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
          Main Database ({{ engineName }})
        </h1>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {{ engineName }} server information and statistics
        </p>
      </div>

      <!-- Server Info -->
      <div class="mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800">
        <h2 class="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
          {{ engineName }} Server
        </h2>
        <div class="grid grid-cols-2 gap-4 md:grid-cols-3">
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Version</div>
            <div class="font-mono text-lg text-gray-900 dark:text-white">
              {{ engineVersion }}
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Mode</div>
            <div class="font-mono text-lg text-gray-900 dark:text-white">
              {{ databaseMetrics.redis_info.redis_mode }}
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Uptime</div>
            <div class="font-mono text-lg text-gray-900 dark:text-white">
              {{ databaseMetrics.redis_info.uptime_in_days }} days
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Connected Clients</div>
            <div class="font-mono text-lg text-gray-900 dark:text-white">
              {{ databaseMetrics.redis_info.connected_clients }}
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Ops/sec</div>
            <div class="font-mono text-lg text-gray-900 dark:text-white">
              {{ databaseMetrics.redis_info.instantaneous_ops_per_sec }}
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Total Commands</div>
            <div class="font-mono text-lg text-gray-900 dark:text-white">
              {{ databaseMetrics.redis_info.total_commands_processed.toLocaleString() }}
            </div>
          </div>
        </div>
      </div>

      <!-- Memory Stats -->
      <div class="mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800">
        <h2 class="mb-4 text-xl font-semibold text-gray-900 dark:text-white">Memory</h2>
        <div class="grid grid-cols-2 gap-4 md:grid-cols-3">
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Used Memory</div>
            <div class="font-mono text-lg text-gray-900 dark:text-white">
              {{ databaseMetrics.memory_stats.used_memory_human }}
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">RSS Memory</div>
            <div class="font-mono text-lg text-gray-900 dark:text-white">
              {{ databaseMetrics.memory_stats.used_memory_rss_human }}
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Peak Memory</div>
            <div class="font-mono text-lg text-gray-900 dark:text-white">
              {{ databaseMetrics.memory_stats.used_memory_peak_human }}
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Fragmentation Ratio</div>
            <div class="font-mono text-lg text-gray-900 dark:text-white">
              {{ databaseMetrics.memory_stats.mem_fragmentation_ratio.toFixed(2) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Database Sizes -->
      <div class="mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800">
        <h2 class="mb-4 text-xl font-semibold text-gray-900 dark:text-white">Databases</h2>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div
            v-for="(dbInfo, dbName) in databaseMetrics.database_sizes"
            :key="dbName"
            class="rounded border border-gray-200 p-4 dark:border-gray-700">
            <div class="font-semibold text-gray-900 dark:text-white">{{ dbName }}</div>
            <div class="mt-2 text-sm text-gray-600 dark:text-gray-400">
              <template v-if="dbInfo !== null && typeof dbInfo === 'object' && dbInfo.keys !== undefined">
                {{ dbInfo.keys.toLocaleString() }} keys, {{ dbInfo.expires.toLocaleString() }} with
                TTL
              </template>
              <template v-else>
                {{ dbInfo }}
              </template>
            </div>
          </div>
        </div>
        <div class="mt-4 text-sm text-gray-600 dark:text-gray-400">
          Total keys: {{ databaseMetrics.total_keys.toLocaleString() }}
        </div>
      </div>

      <!-- Model Counts -->
      <div class="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
        <h2 class="mb-4 text-xl font-semibold text-gray-900 dark:text-white">Model Counts</h2>
        <div class="grid grid-cols-3 gap-4">
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Customers</div>
            <div class="text-2xl font-bold text-gray-900 dark:text-white">
              {{ databaseMetrics.model_counts.customers.toLocaleString() }}
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Secrets</div>
            <div class="text-2xl font-bold text-gray-900 dark:text-white">
              {{ databaseMetrics.model_counts.secrets.toLocaleString() }}
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Metadata</div>
            <div class="text-2xl font-bold text-gray-900 dark:text-white">
              {{ databaseMetrics.model_counts.receipts.toLocaleString() }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="text-center text-gray-600 dark:text-gray-400">
      Failed to load database metrics
    </div>
  </div>
</template>
